import React from "react";
import { useNavigate } from "react-router-dom";
import { useCompanyContext } from "../../common/company/CompanyContext";
import ToggleSection from "../../common/toggle/ToggleSection";
import { PILLAR_COLORS } from "../../site-admin/report_management/blueprint/blueprintReportingTemplates";
import HrSyncController from "./HrSyncController";
import HrToolLogos from "./HrToolLogos";
import { HR_CODE_KINDS, HR_METRICS, HR_METRIC_GROUPS } from "./HrSyncDTO";

/**
 * 🎯 HR & WORKFORCE
 * Where a company's people figures come from — three neat sections, all closed:
 *   • HR system connectors (only usable once a global admin has switched one on)
 *   • Map your HR codes (no connector? map your own codes to the ESG HR metrics)
 *   • Enter HR figures manually (for one reporting period)
 * Everything lands in HR_SYNC.Metric and fills the Social topic forms.
 */
const ACCENT = PILLAR_COLORS.S.main;

function ConnectorSection({ c }) {
  const p = c.provider;
  return (
    <>
      {!c.available && (
        <div className="alert alert-secondary small">
          No HR connector is switched on for this site yet. You can still map your own HR codes or enter the figures by hand below.
        </div>
      )}

      {c.available && !c.active && (
        <div className="d-flex flex-wrap align-items-center gap-3">
          <p className="small mb-0 flex-grow-1">
            Connect your HR system once; Sustina then reads the headcount, contracts, hires, leavers and absence it needs —
            as totals only, never personal records.
          </p>
          <button type="button" className="btn btn-sm text-white fw-bold" style={{ backgroundColor: ACCENT }} disabled={!!c.busy} onClick={c.connect}>
            {c.busy === "connect" ? "Connecting…" : "Connect your HR system"}
          </button>
        </div>
      )}

      {c.active && (
        <div className="d-flex flex-wrap align-items-center gap-3">
          <div className="flex-grow-1">
            <div className="fw-bold">{c.active.toolName}</div>
            <div className="small text-muted">
              Status {c.active.status}
              {c.active.lastSyncAt ? ` · last pulled ${new Date(c.active.lastSyncAt).toLocaleString()}` : " · not pulled yet"}
              {c.active.lastSyncMessage ? ` · ${c.active.lastSyncMessage}` : ""}
            </div>
          </div>
          {c.active.canManage && (
            <>
              <button type="button" className="btn btn-sm text-white fw-bold" style={{ backgroundColor: ACCENT }} disabled={!!c.busy} onClick={c.sync}>
                {c.busy === "sync" ? "Pulling…" : `Pull figures for ${c.period.from.slice(0, 4)}`}
              </button>
              <button type="button" className="btn btn-sm btn-outline-danger" disabled={!!c.busy} onClick={c.disconnectNow}>Disconnect</button>
            </>
          )}
        </div>
      )}

      {p?.canAdminister && (
        <div className="border rounded p-3 mt-3 bg-light">
          <div className="fw-bold small mb-1">Sustina HR connector — site configuration</div>
          <p className="extra-small text-muted mb-2">
            Global administrators only. The account key of the unified HR connector ({p.displayName}); it is stored encrypted and never shown again.
          </p>
          <div className="row g-2 align-items-end">
            <div className="col-md-4">
              <label className="form-label extra-small fw-bold text-muted mb-1" htmlFor="hr-base">API address</label>
              <input id="hr-base" className="form-control form-control-sm" value={c.providerForm.apiBaseUrl}
                onChange={(e) => c.setProviderForm((f) => ({ ...f, apiBaseUrl: e.target.value }))} />
            </div>
            <div className="col-md-4">
              <label className="form-label extra-small fw-bold text-muted mb-1" htmlFor="hr-key">
                API key {p.hasKey && <span className="text-success">(stored — leave blank to keep)</span>}
              </label>
              <input id="hr-key" type="password" autoComplete="off" className="form-control form-control-sm" value={c.providerForm.apiKey}
                onChange={(e) => c.setProviderForm((f) => ({ ...f, apiKey: e.target.value }))} />
            </div>
            <div className="col-md-2">
              <div className="form-check form-switch mb-1">
                <input id="hr-on" className="form-check-input" type="checkbox" checked={c.providerForm.isEnabled}
                  onChange={(e) => c.setProviderForm((f) => ({ ...f, isEnabled: e.target.checked }))} />
                <label className="form-check-label small" htmlFor="hr-on">Switched on</label>
              </div>
            </div>
            <div className="col-md-2">
              <button type="button" className="btn btn-sm btn-dark w-100" disabled={!!c.busy} onClick={c.saveProvider}>
                {c.busy === "provider" ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function MetricSelect({ value, onChange, disabled }) {
  return (
    <select className="form-select form-select-sm" value={value || ""} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
      <option value="">— not mapped —</option>
      {HR_METRIC_GROUPS.map((grp) => (
        <optgroup key={grp.key} label={grp.label}>
          {HR_METRICS.filter((m) => m.group === grp.key).map((m) => (
            <option key={m.code} value={m.code}>{m.label}</option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

function CodesSection({ c }) {
  const kindHint = HR_CODE_KINDS.find((k) => k.code === c.newCode.kind)?.hint;
  return (
    <>
      <p className="small text-muted">
        List the codes your HR or payroll system uses and say which ESG figure each one counts towards — for example
        contract type <strong>FIXED</strong> → <em>Temporary contracts</em>, absence type <strong>ACC</strong> → <em>Days lost to work injury</em>.
        The mapping is kept for your company and reused every period.
      </p>

      <div className="row g-2 align-items-end mb-3">
        <div className="col-md-3">
          <label className="form-label extra-small fw-bold text-muted mb-1">Kind of code</label>
          <select className="form-select form-select-sm" value={c.newCode.kind} onChange={(e) => c.setNewCode((n) => ({ ...n, kind: e.target.value }))}>
            {HR_CODE_KINDS.map((k) => <option key={k.code} value={k.code}>{k.label}</option>)}
          </select>
        </div>
        <div className="col-md-2">
          <label className="form-label extra-small fw-bold text-muted mb-1">Code</label>
          <input className="form-control form-control-sm" placeholder={kindHint || ""} value={c.newCode.code}
            onChange={(e) => c.setNewCode((n) => ({ ...n, code: e.target.value }))} />
        </div>
        <div className="col-md-5">
          <label className="form-label extra-small fw-bold text-muted mb-1">Description</label>
          <input className="form-control form-control-sm" value={c.newCode.description}
            onChange={(e) => c.setNewCode((n) => ({ ...n, description: e.target.value }))} />
        </div>
        <div className="col-md-2">
          <button type="button" className="btn btn-sm btn-dark w-100" disabled={!!c.busy} onClick={c.addCode}>Add code</button>
        </div>
      </div>

      <details className="mb-3">
        <summary className="small fw-bold">Paste a list of codes</summary>
        <p className="extra-small text-muted mt-2 mb-1">
          One per line: <code>code, description</code> (uses the kind picked above) or <code>KIND, code, description</code>.
        </p>
        <textarea rows={4} className="form-control form-control-sm mb-2" value={c.pasteText} onChange={(e) => c.setPasteText(e.target.value)} />
        <button type="button" className="btn btn-sm btn-outline-dark" disabled={!!c.busy} onClick={c.importPasted}>Import</button>
      </details>

      {c.codes.length === 0 ? (
        <p className="small text-muted fst-italic mb-0">No HR codes yet.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr className="extra-small text-uppercase text-muted">
                <th>Kind</th><th>Code</th><th>Description</th><th style={{ minWidth: 260 }}>Counts towards</th><th />
              </tr>
            </thead>
            <tbody>
              {c.codes.map((code) => (
                <tr key={code.id}>
                  <td className="small">{HR_CODE_KINDS.find((k) => k.code === code.kind)?.label || code.kind}</td>
                  <td className="small fw-bold">{code.code}</td>
                  <td className="small">{code.description || "—"}</td>
                  <td><MetricSelect value={code.target} disabled={c.busy === `map:${code.id}`} onChange={(v) => c.setTarget(code, v)} /></td>
                  <td className="text-end">
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => c.dropCode(code)}>Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function ManualSection({ c }) {
  return (
    <>
      <p className="small text-muted">
        Type in the figures for <strong>{c.period.from} → {c.period.to}</strong>. A figure pulled from a connected HR system is
        marked as such; saving here replaces it with your number. Leave a box empty to clear it.
      </p>
      {HR_METRIC_GROUPS.map((grp) => (
        <div key={grp.key} className="mb-3">
          <div className="extra-small fw-bold text-uppercase mb-2" style={{ color: ACCENT }}>{grp.label}</div>
          <div className="row g-2">
            {HR_METRICS.filter((m) => m.group === grp.key).map((m) => {
              const stored = c.metricSource[m.code];
              return (
                <div key={m.code} className="col-md-4 col-lg-3">
                  <label className="form-label extra-small fw-bold text-muted mb-1" htmlFor={`hr-m-${m.code}`}>
                    {m.label}
                    {stored && <span className="badge bg-light text-dark border ms-1">{stored.source === "MANUAL" ? "entered" : stored.toolName || "HR system"}</span>}
                  </label>
                  <div className="input-group input-group-sm">
                    <input id={`hr-m-${m.code}`} type="number" inputMode="decimal" className="form-control"
                      value={c.manual[m.code] ?? ""} onChange={(e) => c.setManualValue(m.code, e.target.value)} />
                    <span className="input-group-text">{m.unit}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <button type="button" className="btn btn-sm text-white fw-bold" style={{ backgroundColor: ACCENT }} disabled={!!c.busy} onClick={c.saveManual}>
        {c.busy === "manual" ? "Saving…" : "Save figures"}
      </button>
    </>
  );
}

export default function HrSyncView() {
  const navigate = useNavigate();
  const { activeCompanyId, activeCompanyName } = useCompanyContext();
  const c = HrSyncController(activeCompanyId, activeCompanyName);

  if (!c.hasCompany) {
    navigate("/registered/dashboard");
    return null;
  }

  const filled = c.metrics.length;
  return (
    <main id="main" className="page-main" data-scroll-container>
      <div className="page-main-inner">
        <section className="page-section">
          <div className="container mt-4">
            <h2 className="h4 fw-bold mb-1">HR &amp; Workforce</h2>
            <p className="text-muted small mb-3">
              Where <strong>{activeCompanyName || "this company"}</strong>'s people figures come from — they fill the Social
              topics (Own Workforce, Health &amp; Safety, Training, Recruitment).
            </p>

            <HrToolLogos />

            <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
              <div>
                <label className="form-label extra-small fw-bold text-muted mb-1" htmlFor="hr-from">Reporting period — from</label>
                <input id="hr-from" type="date" className="form-control form-control-sm" value={c.period.from}
                  onChange={(e) => c.setPeriod((p) => ({ ...p, from: e.target.value }))} />
              </div>
              <div>
                <label className="form-label extra-small fw-bold text-muted mb-1" htmlFor="hr-to">to</label>
                <input id="hr-to" type="date" className="form-control form-control-sm" value={c.period.to}
                  onChange={(e) => c.setPeriod((p) => ({ ...p, to: e.target.value }))} />
              </div>
              <span className="small text-muted mb-1">{filled} of {HR_METRICS.length} figures held for this period</span>
            </div>

            {c.error && <div className="alert alert-danger small">{c.error}</div>}
            {c.notice && <div className="alert alert-success small">{c.notice}</div>}

            <ToggleSection
              accent={ACCENT}
              title="HR system connectors"
              aside={c.active ? `${c.active.toolName} connected` : c.available ? "available" : "none switched on"}
            >
              <ConnectorSection c={c} />
            </ToggleSection>

            <ToggleSection accent={ACCENT} title="Map your HR codes" aside={`${c.mappedCount}/${c.codes.length} mapped`}>
              <CodesSection c={c} />
            </ToggleSection>

            <ToggleSection accent={ACCENT} title="Enter HR figures manually" aside={`${filled} held`}>
              <ManualSection c={c} />
            </ToggleSection>

            <div className="mt-5 pt-4 border-top">
              <button className="btn btn-link text-decoration-none p-0 text-secondary small" onClick={() => navigate("/registered/company-dashboard")}>
                <i className="bi bi-arrow-left me-2"></i> Return to Company Dashboard
              </button>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}

